import { useCallback, useEffect, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid2,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, ViewModule } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Bloco, BlocoPayload, emptyBloco } from "../types";
import { useModalStyles } from "../../components/modalStyles";

type FormErrors = Partial<Record<keyof BlocoPayload, string>>;

interface ModalBlocosProps {
  open: boolean;
  loading?: boolean;
  blocoData?: Bloco | null;
  onClose: () => void;
  onSave: (payload: BlocoPayload, id?: number) => void;
}

export default function ModalBlocos(props: ModalBlocosProps) {
  const { open, loading = false, blocoData, onClose, onSave } = props;

  const [form, setForm] = useState<BlocoPayload>(emptyBloco);
  const [errors, setErrors] = useState<FormErrors>({});

  const theme = useTheme();
  const styles = useModalStyles(theme);
  const isEdit = Boolean(blocoData?.id);

  const validate = useCallback((data: BlocoPayload): FormErrors => {
    const errs: FormErrors = {};

    if (!data.nome?.trim()) errs.nome = "Nome é obrigatório";

    return errs;
  }, []);

  useEffect(() => {
    if (!open) return;

    let initialData: BlocoPayload = emptyBloco;

    if (blocoData) {
      const { id, ...rest } = blocoData;
      initialData = { ...emptyBloco, ...rest };
    }

    setForm(initialData);
    setErrors(validate(initialData));
  }, [open, blocoData, validate]);

  const handleChange = (field: keyof BlocoPayload, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setErrors(validate(next));
  };

  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    onSave(
      {
        nome: form.nome.trim(),
        blocos: form.blocos.trim(),
      },
      blocoData?.id,
    );
  };

  const fields: {
    field: keyof BlocoPayload;
    label: string;
    placeholder: string;
    size: { xs: number; sm: number };
    multiline?: boolean;
  }[] = [
    {
      field: "nome",
      label: "Nome",
      placeholder: "Digite o nome do bloco",
      size: { xs: 12, sm: 12 },
    },
    {
      field: "blocos",
      label: "Blocos",
      placeholder: "Digite o bloco",
      size: { xs: 12, sm: 12 },
      multiline: true,
    },
  ];

  return (
    <Modal open={open} onClose={loading ? () => {} : onClose}>
      <Paper sx={{ ...styles.paper, minHeight: "fit-content" }}>
        <Stack sx={styles.boxHeader}>
          <Stack sx={styles.boxTitle}>
            <Stack
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ViewModule color="primary" />
            </Stack>
            <Stack>
              <Typography
                fontWeight="bold"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                {isEdit ? "Editar bloco" : "Adicionar bloco"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {form.nome || "Novo bloco"}
              </Typography>
            </Stack>
          </Stack>
          <IconButton aria-label="close" onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Stack>

        <Divider sx={styles.divider} />

        <Grid2 container columnSpacing={1} sx={styles.boxFields}>
          {fields.map(({ field, label, placeholder, size, multiline }) => (
            <Grid2 size={size} key={field}>
              <Typography variant="subtitle2">{label}</Typography>
              <TextField
                fullWidth
                size="small"
                autoComplete="off"
                placeholder={placeholder}
                value={form[field] ?? ""}
                onChange={(event) => handleChange(field, event.target.value)}
                error={Boolean(errors[field])}
                helperText={errors[field] ?? " "}
                {...(multiline ? { multiline: true, minRows: 2 } : {})}
              />
            </Grid2>
          ))}
        </Grid2>

        <Box sx={styles.boxActions}>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={hasErrors || loading}
            onClick={handleSave}
          >
            {isEdit ? "Salvar" : "Adicionar"}
          </LoadingButton>
        </Box>
      </Paper>
    </Modal>
  );
}
